import React, { useCallback, useEffect, useState } from 'react';
import {
  decideLOAIdentityReview,
  getLOAIdentityReviews,
  LOAIdentityAddress,
  LOAIdentityEvidenceAttachment,
  LOAIdentityName,
  LOAIdentityReviewRecord,
  LOAIdentityReviewsResponse,
  LOAIdentityReviewState,
} from '../api/claims';

interface Props {
  claimId: string;
}

const STATE_LABELS: Record<LOAIdentityReviewState, string> = {
  staging: 'Preparing review',
  pending_review: 'Pending review',
  approved: 'Approved',
  processing: 'Processing',
  completed: 'Completed',
  rejected: 'Rejected',
  processing_failed: 'Processing failed',
};

const text = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return 'Not supplied';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const nameText = (name: LOAIdentityName | undefined): string => {
  if (!name) return 'Not supplied';
  return [name.title, name.firstName, name.middleName, name.lastName || name.secondName]
    .filter(Boolean)
    .join(' ') || 'Not supplied';
};

const addressText = (address: LOAIdentityAddress | undefined): string => {
  if (!address) return 'Not supplied';
  return [
    address.line1 || address.addressLine1,
    address.line2 || address.addressLine2,
    address.city || address.town,
    address.county,
    address.postcode,
  ].filter(Boolean).join(', ') || 'Not supplied';
};

const dateText = (value: string | undefined): string => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('en-GB');
};

const errorText = (error: any): string => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.error;
  if (status === 409 || status === 503) {
    return `${detail || 'The decision could not be confirmed.'} Refresh the review before taking any further action.`;
  }
  return `${detail || 'The identity review decision could not be recorded.'} Refresh the review before trying again.`;
};

const attachmentName = (attachment: LOAIdentityEvidenceAttachment, index: number): string =>
  String(attachment.name || attachment.filename || attachment.blobName || `ID document ${index + 1}`);

const NAME_FIELDS = [
  ['title', 'Title'], ['firstName', 'First name'], ['middleName', 'Middle name'],
  ['lastName', 'Surname (lastName)'], ['secondName', 'Surname (secondName)'],
] as const;

const historyText = (items: Array<string | LOAIdentityName | LOAIdentityAddress> | undefined): string => {
  if (!items) return 'Not supplied';
  if (!items.length) return 'None supplied';
  return items.map(item => typeof item === 'string' ? text(item)
    : Object.entries(item).map(([key, value]) => `${key}: ${text(value)}`).join('; ') || 'Not supplied')
    .join('\n');
};

const ReviewComparison: React.FC<{ review: LOAIdentityReviewRecord }> = ({ review }) => {
  const submission = review.submission || {};
  const submittedName = submission.currentNames?.map(nameText).join('; ') || 'Not supplied';
  const submittedAddress = addressText(submission.currentAddress);

  return (
    <>
      <h5 style={{ marginBottom: 6 }}>Original and submitted identity</h5>
      {review.claimIds.map(claimId => {
        const original = review.originals?.[claimId] || {};
        const originalName = nameText(original);
        const originalAddress = addressText({
          addressLine1: original.addressLine1,
          addressLine2: original.addressLine2,
          city: original.city,
          county: original.county,
          postcode: original.postcode,
        });
        return (
          <div key={claimId} style={{ borderTop: '1px solid #d7dee3', paddingTop: 10, marginTop: 10 }}>
            <strong>{claimId}</strong>{claimId === review.primaryClaimId ? ' (primary)' : ''}
            <table style={{ width: '100%', marginTop: 6, fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr><th style={{ textAlign: 'left' }}>Field</th><th style={{ textAlign: 'left' }}>Original</th><th style={{ textAlign: 'left' }}>Submitted</th></tr>
              </thead>
              <tbody>
                <tr><td>Name</td><td>{originalName}</td><td>{submittedName}</td></tr>
                {NAME_FIELDS.map(([field, label]) => (
                  <tr key={field}><td>{label}</td><td>{text(original[field])}</td>
                    <td>{submission.currentNames?.map(name => text(name[field])).join('; ') || 'Not supplied'}</td></tr>
                ))}
                <tr><td>Full legal name</td><td>{text(original.fullLegalName)}</td><td>{text(submission.fullLegalName)}</td></tr>
                <tr><td>Date of birth</td><td>{text(original.dateOfBirth)}</td><td>{text(submission.dateOfBirth)}</td></tr>
                <tr><td>Address</td><td>{originalAddress}</td><td>{submittedAddress}</td></tr>
                <tr><td>Previous names</td><td style={{ whiteSpace: 'pre-wrap' }}>{historyText(original.previousNames)}</td><td style={{ whiteSpace: 'pre-wrap' }}>{historyText(submission.previousNames)}</td></tr>
                <tr><td>Previous addresses</td><td style={{ whiteSpace: 'pre-wrap' }}>{historyText(original.previousAddresses)}</td><td style={{ whiteSpace: 'pre-wrap' }}>{historyText(submission.previousAddresses)}</td></tr>
              </tbody>
            </table>
            <div style={{ marginTop: 6 }}>
              <strong>Reasons for review</strong>
              {(review.reasons?.[claimId] || []).length ? (
                <ul style={{ marginTop: 4 }}>
                  {review.reasons[claimId].map((reason, index) => <li key={`${reason}-${index}`}>{reason}</li>)}
                </ul>
              ) : <div>No comparison reason recorded.</div>}
            </div>
          </div>
        );
      })}

      <h5 style={{ marginBottom: 6 }}>Whole submitted form</h5>
      <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(130px, auto) 1fr', gap: '4px 10px', fontSize: 13 }}>
        <dt>Current names</dt><dd style={{ margin: 0 }}>{submittedName}</dd>
        <dt>Full legal name</dt><dd style={{ margin: 0 }}>{text(submission.fullLegalName)}</dd>
        <dt>Date of birth</dt><dd style={{ margin: 0 }}>{text(submission.dateOfBirth)}</dd>
        <dt>Current address</dt><dd style={{ margin: 0 }}>{submittedAddress}</dd>
        <dt>Previous names</dt><dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{historyText(submission.previousNames)}</dd>
        <dt>Previous addresses</dt><dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{historyText(submission.previousAddresses)}</dd>
        <dt>Vehicles</dt>
        <dd style={{ margin: 0 }}>
          {submission.vehicles?.map(vehicle => [vehicle.claimId, vehicle.make, vehicle.model, vehicle.registration].filter(Boolean).join(' ')).join('; ') || 'None supplied'}
        </dd>
        <dt>Consent</dt>
        <dd style={{ margin: 0 }}>
          {submission.consent
            ? Object.entries(submission.consent).map(([key, value]) => `${key}: ${text(value)}`).join('; ')
            : 'Not supplied'}
        </dd>
      </dl>
    </>
  );
};

const LOAIdentityReviewPanel: React.FC<Props> = ({ claimId }) => {
  const [data, setData] = useState<LOAIdentityReviewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busyRequest, setBusyRequest] = useState<string | null>(null);
  const [lockedRequests, setLockedRequests] = useState<Set<string>>(new Set());
  const [decisionErrors, setDecisionErrors] = useState<Record<string, string>>({});
  const [decisionNotices, setDecisionNotices] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await getLOAIdentityReviews(claimId);
      setData({ reviews: response.reviews || [], idEvidence: response.idEvidence || {} });
      setLockedRequests(new Set());
      setDecisionErrors({});
      setDecisionNotices({});
    } catch (error: any) {
      setLoadError(error?.response?.data?.error || 'Identity reviews could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [claimId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const decide = async (review: LOAIdentityReviewRecord, decision: 'approve' | 'reject') => {
    const reason = (reasons[review.requestId] || '').trim();
    if (!reason || busyRequest || lockedRequests.has(review.requestId)) return;
    setBusyRequest(review.requestId);
    setDecisionErrors(current => ({ ...current, [review.requestId]: '' }));
    try {
      const response = await decideLOAIdentityReview(claimId, review.requestId, decision, reason);
      setData(current => current ? {
        ...current,
        reviews: current.reviews.map(item => item.requestId === review.requestId ? response.review : item),
      } : current);
      setDecisionNotices(current => ({
        ...current,
        [review.requestId]: decision === 'approve'
          ? 'Approval recorded. Refresh to view processing status.'
          : 'Rejection recorded. Refresh to confirm the latest review status.',
      }));
    } catch (error: any) {
      setDecisionErrors(current => ({ ...current, [review.requestId]: errorText(error) }));
      setLockedRequests(current => new Set(current).add(review.requestId));
    } finally {
      setBusyRequest(null);
    }
  };

  if (!loading && !loadError && (!data || data.reviews.length === 0)) return null;

  return (
    <section
      aria-label="LOA identity review"
      style={{ marginTop: 20, padding: 12, border: '1px solid #f0b429', borderRadius: 6, background: '#fffaf0' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h4 style={{ margin: 0 }}>LOA identity review</h4>
        <button type="button" onClick={() => void refresh()} disabled={loading}>Refresh review</button>
      </div>
      {loading && <p>Loading identity reviews…</p>}
      {loadError && <p role="alert">{loadError}</p>}
      {!loading && data?.reviews.map(review => {
        const evidence = review.clientId ? data.idEvidence[review.clientId] : undefined;
        const reason = reasons[review.requestId] || '';
        const locked = lockedRequests.has(review.requestId);
        const pending = review.state === 'pending_review';
        return (
          <article key={review.requestId} style={{ marginTop: 12, padding: 12, background: '#fff', border: '1px solid #e0d7c5', borderRadius: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <strong>{STATE_LABELS[review.state] || review.state.replaceAll('_', ' ')}</strong>
              <span style={{ fontSize: 12 }}>Request {review.requestId}</span>
            </div>
            <p><strong>Affected claims:</strong> {review.claimIds.join(', ')}</p>

            <ReviewComparison review={review} />

            <div style={{ marginTop: 14, padding: 10, background: '#f5f8fa', borderRadius: 4 }}>
              <h5 style={{ margin: '0 0 6px' }}>Existing ID evidence</h5>
              <p style={{ margin: '0 0 6px' }}>
                ID verification status does not prove that the submitted name or date of birth matches the ID. Extracted identity values are unavailable.
              </p>
              <div><strong>Status:</strong> {evidence?.status || 'Unavailable'}</div>
              <div><strong>Verified at:</strong> {dateText(evidence?.verifiedAt)}</div>
              <div><strong>Evidence task:</strong> {evidence?.taskId || 'Not recorded'}</div>
              <div><strong>Notes:</strong> {evidence?.notes || 'No prior verification notes are available.'}</div>
              {evidence?.attachments?.length ? (
                <ul>
                  {evidence.attachments.map((attachment, index) => {
                    const url = attachment.url || attachment.downloadUrl;
                    const label = attachmentName(attachment, index);
                    return <li key={`${label}-${index}`}>{url
                      ? <a href={url} target="_blank" rel="noopener noreferrer">{label}</a>
                      : <span>{label} (link unavailable)</span>}</li>;
                  })}
                </ul>
              ) : <p>No ID document links are available.</p>}
            </div>

            {review.decision && (
              <p><strong>Decision:</strong> {review.decision.reason || 'No reason recorded'} — {review.decision.actor || 'Unknown reviewer'} at {dateText(review.decision.at)}</p>
            )}
            {decisionNotices[review.requestId] && <p role="status">{decisionNotices[review.requestId]}</p>}
            {decisionErrors[review.requestId] && <p role="alert">{decisionErrors[review.requestId]}</p>}

            {pending && (
              <div style={{ marginTop: 12 }}>
                <label htmlFor={`loa-review-reason-${review.requestId}`}><strong>Decision reason</strong></label>
                <textarea
                  id={`loa-review-reason-${review.requestId}`}
                  value={reason}
                  maxLength={2000}
                  onChange={event => setReasons(current => ({ ...current, [review.requestId]: event.target.value }))}
                  disabled={busyRequest === review.requestId || locked}
                  style={{ display: 'block', width: '100%', minHeight: 70, marginTop: 4, boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => void decide(review, 'approve')}
                    disabled={!reason.trim() || busyRequest !== null || locked}
                  >Approve identity changes</button>
                  <button
                    type="button"
                    onClick={() => void decide(review, 'reject')}
                    disabled={!reason.trim() || busyRequest !== null || locked}
                  >Reject identity changes</button>
                </div>
                {!reason.trim() && <small>A reason is required before approving or rejecting.</small>}
              </div>
            )}
          </article>
        );
      })}
    </section>
  );
};

export default LOAIdentityReviewPanel;
